import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DataProvider } from '@dhis2/app-runtime';
import { AuthProvider } from './features/auth/AuthContext';
import Layout from './shared/components/Layout';
import Login from './features/auth/Login';
import UserRegistration from './features/organisationUsers/components/UserRegistrationForm';
import OrganisationRegistration from './features/organisationUsers/components/OrganisationRegistration';
import DashboardPage from './features/dashboard/DashboardPage';
import PersonalPerformanceNotes from './features/objectivesWeeklyReporting/PersonalPerformanceNotes';
import ManagerPPNView from './features/objectivesWeeklyReporting/components/ManagerPPNView';         
import KPIs from './features/kpisAndKras/KPIPage';                            
import KRAs from './features/kpisAndKras/KRAPage';                            
import MyObjectivesPage from './features/progress/MyObjectivessPage';
import SuperuserOrgManagement from './features/superuserOrganisation/SuperuserOrgManagement';
import PerformanceModule from './features/performance/PerformanceModule';
import LeadershipManagementPage from './features/leadership/LeadershipManagementPage';
import MyReportsPage from './features/reports/MyReportsPage';
import TasksReportPage from './features/reports/TasksReportPage';
import ObjectivesReportPage from './features/reports/ObjectivesReportPage';
import LeadershipReportPage from './features/reports/LeadershipReportPage';
import AppraisalReportPage from './features/reports/AppraisalReportPage';
import StaffPage from './features/staff/StaffPage';
import {
  ProtectedRoute,
  RegistrationGuard,
  SuperuserOnlyRoute,
  SettingsAccessRoute
} from './shared/components/AccessControl';
import ErrorBoundary from './shared/components/ErrorBoundary';
import OrganisationUpdate from './features/organisationUsers/components/OrganisationUpdate';
import GoalsManagementPage from './features/GoalsManagement/GoalsManagementPage';
import { SessionProvider} from "./features/auth/SessionContext";
import MyProfileEdit from './features/myProfile/MyProfileEdit';
import PerformanceAppraisalPage from './features/appraisal/PerformanceAppraisalPage';
import SetPasswordPage from './features/auth/SetPasswordPage';
import { PerformanceProvider } from './features/performance/PerformanceContext';
import { VerificationSent } from './features/organisationUsers/components/VerificationSent';
import { FiscalYearProvider } from './shared/FiscalYearContext';
import CheckInForm from './features/checkIns/components/CheckInForm';
import CheckInDashboard from './features/checkIns/components/CheckInDashboard';


const dhis2BaseUrl =
  process.env.REACT_APP_DHIS2_BASE_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');
const dhis2ApiVersion = parseInt(process.env.REACT_APP_DHIS2_API_VERSION, 10) || 39;

const App = () => {
 if (process.env.NODE_ENV !== 'production') {
   console.log('[TRACE] App DataProvider config', {
     configuredBaseUrl: dhis2BaseUrl,
     configuredApiVersion: dhis2ApiVersion,
     envBaseUrl: process.env.REACT_APP_DHIS2_BASE_URL,
     envApiVersion: process.env.REACT_APP_DHIS2_API_VERSION,
     locationHref: window.location.href,
     locationOrigin: window.location.origin,
     locationPathname: window.location.pathname
   });
 }

 return (
     <ErrorBoundary>
      <DataProvider
        config={{
          baseUrl: dhis2BaseUrl,
          apiVersion: dhis2ApiVersion,
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }}
      >
        <FiscalYearProvider>
        <BrowserRouter>
          <SessionProvider>
            <AuthProvider>
             <PerformanceProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<UserRegistration />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              <Route path="/verification-sent" element={<VerificationSent />} />

              {/* Protected section */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  {/* Organisation registration */}
                  <Route path="/organisation" element={<OrganisationRegistration />} />

                  {/* Main dashboard and features */}
                  <Route element={<RegistrationGuard />}>
                        {/* Existing Dashboard routes */}
                        <Route index element={<DashboardPage />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="performance-appraisal/:type" element={<PerformanceAppraisalPage />} />
                        
                        {/* New pages */}
                        <Route path="ppn" element={<PersonalPerformanceNotes isManagerView={false} />} />
                        <Route path="ppn/manager" element={<ManagerPPNView />} />

                        <Route path="performance-appraisal/:type" element={<PerformanceAppraisalPage />} />
                        <Route path="kpis" element={<KPIs />} />
                        <Route path="kras" element={<KRAs />} />
                        <Route path="tasks" element={<MyReportsPage />} />
                        <Route path="tasks/tasks-report" element={<TasksReportPage />} />
                        <Route path="tasks/objectives-report" element={<ObjectivesReportPage />} />
                        <Route path="tasks/leadership-report" element={<LeadershipReportPage />} />
                        <Route path="/check-in/dashboard" element={<CheckInDashboard />} />
                        <Route path="/check-in/:employeeId/:period" element={<CheckInForm />} />
                        <Route path="tasks/mid-year-report" element={<AppraisalReportPage reportType="mid-year" />} />
                        <Route path="tasks/year-end-report" element={<AppraisalReportPage reportType="year-end" />} />
                        <Route path="my-objectives" element={<MyObjectivesPage />} />
                        <Route path="profile" element={<MyProfileEdit />} />
                                                                                              
                        {/* Existing routes */}
                        <Route path="/performance-management/*" element={<PerformanceModule />} />
                        <Route path="leadership-management" element={<LeadershipManagementPage />} />
                        <Route path="staff" element={<StaffPage />} />
                        <Route path="/settings"
                          element={
                            <SettingsAccessRoute>
                              <OrganisationUpdate />
                            </SettingsAccessRoute>
                          }
                        />
                        <Route path="/goals-management" element={<GoalsManagementPage />} />
                      </Route>

                  {/* Superuser-only section */}
                  <Route
                    path="/superuser-org-management"
                    element={
                      <SuperuserOnlyRoute>
                        <SuperuserOrgManagement />
                      </SuperuserOnlyRoute>
                    }
                  />
                </Route>
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </PerformanceProvider>
            </AuthProvider>
          </SessionProvider>
        </BrowserRouter>
        </FiscalYearProvider>
      </DataProvider>
    </ErrorBoundary>
  );
};

export default App;
